import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  makeStyles, Fab, Paper, TextField, IconButton, Typography, Box,
  CircularProgress, Zoom, Tooltip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Button, Avatar
} from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import { WorkawareApi } from 'apis/WorkawareApi'
import ChatIcon from '@material-ui/icons/Chat'
import CloseIcon from '@material-ui/icons/Close'
import SendIcon from '@material-ui/icons/Send'
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep'
import AspectRatioIcon from '@material-ui/icons/AspectRatio'
import MinimizeIcon from '@material-ui/icons/IndeterminateCheckBox'
import SmartToyIcon from '@material-ui/icons/Android'
import AssignmentIcon from '@material-ui/icons/Assignment'
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import EventNoteIcon from '@material-ui/icons/EventNote'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import SchoolIcon from '@material-ui/icons/School'
import MessageIcon from '@material-ui/icons/Message'

const useStyles = makeStyles((theme) => {
  const isDarkMode = theme.palette.type === 'dark'
  return {
  fab: {
    position: 'fixed' as const,
    zIndex: 1350,
    width: 64,
    height: 64,
    cursor: 'grab' as const,
    userSelect: 'none' as const,
    touchAction: 'none' as const,
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 65%, ${theme.palette.primary.light} 100%)`,
    boxShadow: `0 16px 36px ${fade(theme.palette.primary.dark, 0.32)}`,
    transition: 'box-shadow 0.25s ease',
    '&:hover': {
      boxShadow: `0 20px 42px ${fade(theme.palette.primary.dark, 0.4)}`,
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
    },
    '&:active': {
      cursor: 'grabbing' as const,
    },
  },
  chatWindow: {
    position: 'fixed' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    zIndex: 1351,
    borderRadius: 8,
    background: isDarkMode
      ? `linear-gradient(180deg, ${fade(theme.palette.background.paper, 0.98)} 0%, ${fade(theme.palette.background.default, 0.98)} 100%)`
      : `linear-gradient(180deg, ${fade(theme.palette.background.paper, 0.98)} 0%, ${fade(theme.palette.background.default, 0.98)} 100%)`,
    backdropFilter: 'blur(18px)',
    overflow: 'hidden',
    boxShadow: `0 26px 60px ${fade(isDarkMode ? theme.palette.background.default : theme.palette.primary.dark, 0.22)}`,
    border: `1px solid ${fade(isDarkMode ? theme.palette.primary.light : theme.palette.primary.light, isDarkMode ? 0.15 : 0.28)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  header: {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 58%, ${theme.palette.primary.light} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 2.25),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.14)'}`,
  },
  headerTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  headerAvatar: {
    width: 42,
    height: 42,
    background: fade(theme.palette.common.white, isDarkMode ? 0.12 : 0.16),
    border: `1px solid ${fade(theme.palette.common.white, isDarkMode ? 0.16 : 0.22)}`,
    color: theme.palette.primary.contrastText,
  },
  headerEyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 999,
    background: fade(theme.palette.common.white, isDarkMode ? 0.1 : 0.14),
    color: fade(theme.palette.common.white, isDarkMode ? 0.8 : 0.88),
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
    marginBottom: 4,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.primary.light,
    boxShadow: `0 0 0 4px ${fade(theme.palette.primary.light, 0.2)}`,
  },
  headerTitle: {
    fontSize: '1.08rem',
    fontWeight: 700,
    lineHeight: 1.2,
    fontFamily: '"Public Sans", sans-serif',
  },
  headerSubtitle: {
    fontSize: 12,
    color: fade(theme.palette.common.white, isDarkMode ? 0.7 : 0.8),
    marginTop: 2,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  headerIconBtn: {
    color: theme.palette.primary.contrastText,
    background: fade(theme.palette.common.white, isDarkMode ? 0.08 : 0.1),
    border: `1px solid ${fade(theme.palette.common.white, isDarkMode ? 0.12 : 0.16)}`,
    padding: 8,
    '&:hover': {
      background: fade(theme.palette.common.white, isDarkMode ? 0.14 : 0.18),
    },
  },
  messageArea: {
    flexGrow: 1,
    padding: theme.spacing(2.25),
    overflowY: 'auto' as const,
    background: isDarkMode
      ? `radial-gradient(circle at top, ${fade('#1e3a5f', 0.1)} 0%, ${fade('#1e3a5f', 0)} 38%), linear-gradient(180deg, #1a1f3a 0%, #162238 100%)`
      : `radial-gradient(circle at top, ${fade(theme.palette.primary.light, 0.18)} 0%, ${fade(theme.palette.primary.light, 0)} 38%), linear-gradient(180deg, #F6FAFC 0%, #EEF4F8 100%)`,
    display: 'flex',
    flexDirection: 'column' as const,
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: fade(theme.palette.primary.main, isDarkMode ? 0.2 : 0.3), borderRadius: '10px' },
  },
  optionBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(2.5, 0.5),
  },
  welcomePanel: {
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: 8,
    background: isDarkMode
      ? `linear-gradient(145deg, ${fade('#2a2f4a', 0.98)} 0%, ${fade('#1e3a5f', 0.08)} 100%)`
      : `linear-gradient(145deg, ${fade(theme.palette.background.paper, 0.98)} 0%, ${fade(theme.palette.primary.light, 0.08)} 100%)`,
    border: `1px solid ${fade(isDarkMode ? '#4a5a7a' : theme.palette.primary.light, isDarkMode ? 0.25 : 0.3)}`,
    boxShadow: `0 18px 36px ${fade(isDarkMode ? '#000000' : theme.palette.primary.dark, isDarkMode ? 0.3 : 0.08)}`,
  },
  welcomeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 999,
    background: fade(theme.palette.primary.main, isDarkMode ? 0.18 : 0.12),
    color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: theme.spacing(2),
  },
  welcomeTitle: {
    color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontFamily: '"Public Sans", sans-serif',
  },
  welcomeBody: {
    color: isDarkMode ? '#b0b8c8' : '#557085',
    lineHeight: 1.6,
    marginBottom: theme.spacing(2.5),
  },
  optionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  selectionButton: {
    width: '100%',
    padding: theme.spacing(1.25),
    borderRadius: 8,
    border: `1px solid ${fade(isDarkMode ? '#4a5a7a' : theme.palette.primary.light, isDarkMode ? 0.25 : 0.34)}`,
    textTransform: 'none' as const,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: isDarkMode
      ? `linear-gradient(180deg, ${fade('#2a2f4a', 0.8)} 0%, ${fade('#1e3a5f', 0.08)} 100%)`
      : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${fade(theme.palette.primary.light, 0.08)} 100%)`,
    boxShadow: `0 8px 20px ${fade(isDarkMode ? '#000000' : theme.palette.primary.dark, isDarkMode ? 0.15 : 0.05)}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      background: isDarkMode
        ? `linear-gradient(180deg, ${fade('#3a4f6a', 0.8)} 0%, ${fade('#2a3a5a', 0.12)} 100%)`
        : 'linear-gradient(180deg, #FFFFFF 0%, #EEF7FB 100%)',
      borderColor: isDarkMode ? fade('#6a8abc', 0.5) : fade(theme.palette.primary.main, 0.45),
      boxShadow: `0 14px 28px ${fade(isDarkMode ? '#000000' : theme.palette.primary.dark, isDarkMode ? 0.25 : 0.1)}`,
      transform: 'translateY(-2px)',
    },
  },
  selectionIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${fade(theme.palette.primary.light, isDarkMode ? 0.12 : 0.18)} 0%, ${fade(theme.palette.primary.main, isDarkMode ? 0.1 : 0.14)} 100%)`,
    color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
    marginRight: theme.spacing(1.75),
    flexShrink: 0,
  },
  selectionTextWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    textAlign: 'left' as const,
    minWidth: 0,
    flexGrow: 1,
  },
  selectionTitle: {
    color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark,
    fontWeight: 700,
    fontFamily: '"Public Sans", sans-serif',
  },
  selectionDescription: {
    color: isDarkMode ? '#8a92a8' : '#667C8D',
    marginTop: 3,
    lineHeight: 1.45,
  },
  selectionArrow: {
    color: fade(theme.palette.primary.main, isDarkMode ? 0.4 : 0.55),
    marginLeft: theme.spacing(1),
  },
  hintText: {
    color: isDarkMode ? '#8a92a8' : '#71889A',
    fontSize: 12,
    textAlign: 'center' as const,
    marginTop: theme.spacing(0.5),
  },
  inputArea: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    background: isDarkMode
      ? fade('#1a1f3a', 0.95)
      : fade(theme.palette.background.paper, 0.92),
    borderTop: `1px solid ${fade(isDarkMode ? '#4a5a7a' : theme.palette.primary.light, isDarkMode ? 0.2 : 0.3)}`,
    alignItems: 'center',
    gap: 10,
  },
  aiMsgContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    marginBottom: 14,
    gap: 10,
  },
  botAvatar: {
    width: 36,
    height: 36,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    marginBottom: 4,
    boxShadow: `0 8px 18px ${fade(theme.palette.primary.dark, isDarkMode ? 0.3 : 0.18)}`,
  },
  userMsg: {
    alignSelf: 'flex-end',
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: '12px 16px',
    borderRadius: '22px 22px 6px 22px',
    marginBottom: 14,
    maxWidth: '80%',
    boxShadow: `0 12px 24px ${fade(theme.palette.primary.dark, isDarkMode ? 0.3 : 0.2)}`,
  },
  aiMsg: {
    alignSelf: 'flex-start',
    background: isDarkMode ? fade('#2a3a5a', 0.9) : theme.palette.background.paper,
    color: isDarkMode ? '#d0d8e8' : '#274255',
    padding: '12px 16px',
    borderRadius: '22px 22px 22px 6px',
    maxWidth: '85%',
    boxShadow: `0 10px 24px ${fade(isDarkMode ? '#000000' : theme.palette.primary.dark, isDarkMode ? 0.2 : 0.08)}`,
    border: `1px solid ${fade(isDarkMode ? '#4a5a7a' : theme.palette.primary.light, isDarkMode ? 0.2 : 0.24)}`,
  },
  textFieldRoot: {
    flexGrow: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      background: fade(theme.palette.primary.light, isDarkMode ? 0.04 : 0.06),
      '& fieldset': {
        borderColor: fade(theme.palette.primary.light, isDarkMode ? 0.2 : 0.34),
      },
      '&:hover fieldset': {
        borderColor: fade(theme.palette.primary.main, isDarkMode ? 0.35 : 0.45),
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 12,
      paddingBottom: 12,
      color: isDarkMode ? theme.palette.text.primary : 'inherit',
    },
  },
  sendButton: {
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: `0 10px 20px ${fade(theme.palette.primary.dark, isDarkMode ? 0.25 : 0.2)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    },
    '&.Mui-disabled': {
      background: fade(theme.palette.primary.light, isDarkMode ? 0.15 : 0.28),
      color: fade(theme.palette.primary.dark, isDarkMode ? 0.4 : 0.5),
      boxShadow: 'none',
    },
  },
  dialogPaper: {
    borderRadius: 8,
    padding: 8,
    background: isDarkMode ? theme.palette.background.paper : '#fff',
  },
  dialogTitle: {
    fontWeight: 700,
    color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark,
    fontFamily: '"Public Sans", sans-serif',
  },
  dialogActions: {
    padding: '0 16px 16px',
  },
  dialogCancelBtn: {
    borderRadius: 999,
    textTransform: 'none' as const,
    borderColor: fade(isDarkMode ? '#4a5a7a' : theme.palette.primary.light, isDarkMode ? 0.25 : 0.34),
    color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark,
  },
  dialogClearBtn: {
    borderRadius: 999,
    textTransform: 'none' as const,
    boxShadow: 'none',
    background: isDarkMode ? '#B84E3F' : '#C95A4A',
    '&:hover': {
      background: isDarkMode ? '#A03D2F' : '#B84E3F',
      boxShadow: 'none',
    },
  },
}
})

interface AIChatProps {
  userId: number;
  companyId?: number;
}

const AIChatComponent: React.FC<AIChatProps> = ({ userId, companyId }) => {
  const [isMaximized, setIsMaximized] = useState(false)
  const classes = useStyles({ isMaximized })
  const api = new WorkawareApi()
  const intl = useIntl()
  const localeCode = useSelector((state: IRootReduxState) => state.app.user.localeCode) || 'en'
  const t = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage })

  const [isOpen, setIsOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<{role: string, text: string}[]>([])
  const [loading, setLoading] = useState(false)
  
  const scrollRef = useRef<HTMLDivElement>(null)

  // ── Draggable FAB ──────────────────────────────────────────────────────────
  const [fabPos, setFabPos] = useState<{ x: number; y: number }>(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800
    return {
      x: Math.max(0, vw - 88),
      y: Math.max(0, vh - 120),
    }
  })
  const fabPosRef = useRef(fabPos)
  const dragState = useRef({ isDragging: false, startX: 0, startY: 0, startFabX: 0, startFabY: 0, hasMoved: false })

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const d = dragState.current
    d.isDragging = true
    d.hasMoved = false
    d.startX = e.clientX
    d.startY = e.clientY
    d.startFabX = fabPosRef.current.x
    d.startFabY = fabPosRef.current.y
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    document.body.style.cursor = 'grabbing'
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragState.current
    if (!d.isDragging) return
    const dx = e.clientX - d.startX
    const dy = e.clientY - d.startY
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) d.hasMoved = true
    const newX = Math.max(0, Math.min(window.innerWidth - 64, d.startFabX + dx))
    const newY = Math.max(0, Math.min(window.innerHeight - 64, d.startFabY + dy))
    fabPosRef.current = { x: newX, y: newY }
    setFabPos({ x: newX, y: newY })
  }, [])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragState.current
    if (!d.isDragging) return
    d.isDragging = false
    document.body.style.cursor = ''
    if (!d.hasMoved) setIsOpen(prev => !prev)
  }, [])

  const chatWindowStyle = useMemo((): React.CSSProperties => {
    const FAB = 64, W = 380, H = 600, GAP = 12
    const vw = window.innerWidth, vh = window.innerHeight
    if (isMaximized) {
      return { top: 20, left: 20, right: 20, bottom: 20, width: 'calc(100% - 40px)', height: 'calc(100% - 40px)', maxHeight: '95vh' }
    }
    const { x, y } = fabPosRef.current
    // horizontal: right of FAB if space, else left
    let left = x + FAB + GAP
    if (left + W > vw) left = Math.max(GAP, x - W - GAP)
    left = Math.max(GAP, Math.min(vw - W - GAP, left))
    // vertical: above FAB if space, else below
    let top = y - H - GAP
    if (top < GAP) top = y + FAB + GAP
    top = Math.max(GAP, Math.min(vh - H - GAP, top))
    return { top, left, width: W, height: H, maxHeight: '90vh' }
  }, [fabPos, isMaximized]) // eslint-disable-line react-hooks/exhaustive-deps
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, loading])

  useEffect(() => {
    const handleResize = () => {
      setFabPos(prev => ({
        x: Math.max(0, Math.min(window.innerWidth - 64, prev.x)),
        y: Math.max(0, Math.min(window.innerHeight - 64, prev.y)),
      }))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleSend = async (customMessage?: string) => {
    const textToSend = customMessage || input;
    if (!textToSend.trim() || !userId) return
    
    setMessages((prev) => [...prev, { role: 'user', text: textToSend }])
    if (!customMessage) setInput('')
    setLoading(true)

    try {
      const payload = {
        message: textToSend,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: localeCode,
      };

      const response = await api.ai.chat(payload);

      if (response && response.bot_response) {
        setMessages((prev) => [...prev, { role: 'ai', text: response.bot_response }])
      }
    } catch (error) {
      console.error("Chat API error:", error);
      setMessages((prev) => [...prev, { role: 'ai', text: t('ai.chat.error.unavailable', 'Sorry, service is unavailable.') }])
    } finally {
      setLoading(false)
    }
  }

  const handleClear = async () => {
    setConfirmOpen(false);
    try {
      await api.ai.deleteSession();
    } finally {
      setMessages([]);
    }
  }

  const handleBackToMenu = async () => {
    setMessages([]);
    try {
      await api.ai.deleteSession();
    } catch {
      // session may not exist yet — safe to ignore
    }
  }

  return (
    <>
      <Fab
        color="primary"
        className={classes.fab}
        style={{ left: fabPos.x, top: fabPos.y, bottom: 'auto', right: 'auto' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      <Zoom in={isOpen}>
        <Paper elevation={0} className={classes.chatWindow} style={chatWindowStyle}>
          <Box className={classes.header}>
            <Box className={classes.headerTitleWrap}>
              <Avatar className={classes.headerAvatar}>
                <SmartToyIcon fontSize="small" />
              </Avatar>
              <Box>
                {/* <Box className={classes.headerEyebrow}>
                  <span className={classes.statusDot} />
                  Ready to help
                </Box> */}
                <Typography variant="h6" className={classes.headerTitle}>{t('ai.chat.title', 'AI Assistant')}</Typography>
                <Typography variant="body2" className={classes.headerSubtitle}>
                  {t('ai.chat.subtitle', 'Ask questions, check assignments, and continue your session')}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.headerActions}>
              {messages.length > 0 && (
                <Tooltip title={t('ai.chat.button.back', 'Back to Menu')} arrow placement="bottom">
                  <IconButton size="small" className={classes.headerIconBtn} onClick={handleBackToMenu}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              <IconButton size="small" className={classes.headerIconBtn} onClick={() => setIsMaximized(!isMaximized)}>
                {isMaximized ? <MinimizeIcon fontSize="small" /> : <AspectRatioIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" className={classes.headerIconBtn} onClick={() => setConfirmOpen(true)}>
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" className={classes.headerIconBtn} onClick={() => setIsOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <div className={classes.messageArea} ref={scrollRef}>
            {messages.length === 0 ? (
              <Box className={classes.optionBox}>
                <Box className={classes.welcomePanel}>
                  <Box className={classes.welcomeBadge}>
                    <SmartToyIcon style={{ fontSize: 16 }} />
                    {t('ai.chat.badge', 'Workaware Copilot')}
                  </Box>
                  <Typography variant="h6" className={classes.welcomeTitle}>
                    {t('ai.chat.welcome.title', 'Welcome to Workaware AI')}
                  </Typography>
                  <Typography variant="body2" className={classes.welcomeBody}>
                    {t('ai.chat.welcome.body', 'Start with one of the guided options below, or open a conversation and ask for help in plain language.')}
                  </Typography>
                  <Box className={classes.optionGrid}>
                    {[
                      {
                        icon: <QuestionAnswerIcon />,
                        title: t('ai.chat.option.faq.title', 'FAQ'),
                        description: t('ai.chat.option.faq.description', 'Common WorkAware questions'),
                        prompt: t('ai.chat.option.faq.prompt', 'List the common WorkAware questions you can answer (FAQ topics). Show them as a short bullet list of questions I can ask.'),
                      },
                      {
                        icon: <AssignmentIcon />,
                        title: t('ai.chat.option.assignments.title', 'My Assignments'),
                        description: t('ai.chat.option.assignments.description', 'View assignments assigned to me'),
                        prompt: t('ai.chat.option.assignments.prompt', 'Show my assignments assigned to me'),
                      },
                      {
                        icon: <AddCircleOutlineIcon />,
                        title: t('ai.chat.option.create.title', 'Create Assignment'),
                        description: t('ai.chat.option.create.description', 'Create a new assignment'),
                        prompt: t('ai.chat.option.create.prompt', 'I want to create a new assignment'),
                      },
                      {
                        icon: <EventNoteIcon />,
                        title: t('ai.chat.option.schedule.title', 'Schedule'),
                        description: t('ai.chat.option.schedule.description', 'View or create scheduled items'),
                        prompt: t('ai.chat.option.schedule.prompt', 'Show my scheduled items'),
                      },
                      {
                        icon: <LockOpenIcon />,
                        title: t('ai.chat.option.password.title', 'Reset Password'),
                        description: t('ai.chat.option.password.description', 'Send a password reset email'),
                        prompt: t('ai.chat.option.password.prompt', 'I need to reset a user password'),
                      },
                      {
                        icon: <SchoolIcon />,
                        title: t('ai.chat.option.training.title', 'My Training'),
                        description: t('ai.chat.option.training.description', 'Check certifications and courses'),
                        prompt: t('ai.chat.option.training.prompt', 'Show my training and certification status'),
                      },
                      {
                        icon: <MessageIcon />,
                        title: t('ai.chat.option.message.title', 'Send Message'),
                        description: t('ai.chat.option.message.description', 'Send a direct message to a user'),
                        prompt: t('ai.chat.option.message.prompt', 'I want to send a direct message to a user'),
                      },
                    ].map((item) => (
                      <Button key={item.title} className={classes.selectionButton} onClick={() => handleSend(item.prompt)}>
                        <Box display="flex" alignItems="center" minWidth={0} width="100%">
                          <Box className={classes.selectionIconWrap}>
                            {item.icon}
                          </Box>
                          <Box className={classes.selectionTextWrap}>
                            <Typography variant="body2" className={classes.selectionTitle}>{item.title}</Typography>
                            <Typography variant="caption" className={classes.selectionDescription}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                </Box>
                <Typography className={classes.hintText}>
                   {t('ai.chat.hint.ready', 'WorkAware AI Assistant Ready to help you anytime.')}
                </Typography>
              </Box>
            ) : (
              messages.map((msg, i) => (
                msg.role === 'user' ? (
                  <div key={i} className={classes.userMsg}><Typography variant="body2">{msg.text}</Typography></div>
                ) : (
                  <div key={i} className={classes.aiMsgContainer}>
                    <Avatar className={classes.botAvatar}><SmartToyIcon fontSize="small" /></Avatar>
                    <div className={classes.aiMsg}><Typography variant="body2">{msg.text}</Typography></div>
                  </div>
                )
              ))
            )}
            {loading && (
              <div className={classes.aiMsgContainer}>
                <Avatar className={classes.botAvatar}><SmartToyIcon fontSize="small" /></Avatar>
                <Box className={classes.aiMsg} display="flex" alignItems="center" style={{ gap: '12px' }}>
                  <CircularProgress size={14} thickness={5} />
                  <Typography variant="caption">....</Typography>
                </Box>
              </div>
            )}
          </div>

          {messages.length > 0 && (
            <Box className={classes.inputArea}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textFieldRoot}
                placeholder={t('ai.chat.input.placeholder', 'Type your message...')}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              />
              <IconButton className={classes.sendButton} onClick={() => handleSend()} disabled={loading || !input.trim()}>
                <SendIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
      </Zoom>

      {/* Dialog keeps standard MUI Z-index (1300), which is now higher than the chat window (1250) */}
      <Dialog 
        open={confirmOpen} 
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>{t('ai.chat.dialog.clear.title', 'Clear Session?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ai.chat.dialog.clear.body', 'Are you sure you want to reset this session? All history will be cleared.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            className={classes.dialogCancelBtn}
          >
            {t('ai.chat.dialog.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleClear}
            color="secondary"
            variant="contained"
            className={classes.dialogClearBtn}
          >
            {t('ai.chat.dialog.clear.confirm', 'Yes, Clear')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AIChatComponent;
